// SandboxFrame — parent-side host for the cross-origin dashboard
// sandbox. Replaces the in-page LiveRender so untrusted user TSX no
// longer executes inside the main app's origin.
//
// Architecture:
//   - The iframe loads `DASHBOARD_SANDBOX_URL` (a separate origin, e.g.
//     http://localhost:4300 in dev). The cross-origin barrier is what
//     isolates the dashboard from this app's DOM, RTK store, and
//     cookies. `sandbox="allow-scripts allow-same-origin"` keeps the
//     iframe on its own real origin so its own fonts/assets resolve.
//   - The parent gates iframe mount on the user having a tenant API
//     key set (via the Settings page) or an identity access token. With
//     neither credential, we render an inline authentication prompt.
//   - On mount, the sandbox posts `sandbox-needs-init` and we reply
//     with `sandbox-init` carrying the available auth context plus
//     `{ proxyBaseUrl, branch, site }`.
//     The sandbox uses those to call `/api/v1/ontology/*` directly
//     against the proxy server — no per-request RPC bridge.
//   - When the parent has a templates registry, we also post
//     `sandbox-templates` with `{ templates: [{path, code, defaultValues}] }`
//     so the sandbox's in-scope `<Template path="..." />` primitive can
//     resolve saved templates without any host plumbing.
//   - We `postMessage` the latest code + (optional) props + theme on
//     every change. The sandbox posts `sandbox-ready` once mounted (we
//     re-send then) and `sandbox-error` whenever compile/render fails.
//   - Anchor clicks inside the iframe post `sandbox-navigate` with the
//     raw href. Accepted links assign the parent `window.location` so
//     the whole Axilon document is replaced, including same-origin paths.
//     The iframe sandbox flag does not include top navigation, and the
//     sandbox document URL is a different origin, so the parent is the
//     only place a `/plant` link can land.
//   - Theme is read off `document.documentElement` (`data-theme` /
//     class), the same surface that ThemeProvider writes. A
//     MutationObserver re-posts whenever the user toggles it so the
//     iframe stays in sync.

import { useEffect, useMemo, useRef, useState } from 'react'

import {
  getApiKey,
  getAuthenticationOrganizationId,
  getDashboardSandboxUrl,
  getOntologyBranchHeaderValue,
  getOntologySiteHeaderValue,
  getProxyBaseUrl,
  isDemoMode,
  isIdentityEnabled,
  useAppSelector,
  useActiveBranch,
  useActiveSite
} from '@axilon-apps/domains-ontology-ui-state'

import { followDashboardLink, resolveDashboardLink } from './resolve-dashboard-link'

type SandboxTheme = 'light' | 'dark'

interface SandboxTemplateEntry {
  path: string
  code: string
  defaultValues: Record<string, unknown>
}

interface SandboxRenderMessage {
  type: 'sandbox-render'
  code: string
  theme: SandboxTheme
  props?: Record<string, unknown>
}

interface SandboxInitMessage {
  type: 'sandbox-init'
  apiKey: string
  accessToken?: string
  organizationId: string
  proxyBaseUrl: string
  branch: string
  site: string
  dashboardId?: string
}

interface SandboxTemplatesMessage {
  type: 'sandbox-templates'
  templates: SandboxTemplateEntry[]
}

interface SandboxNeedsInitMessage {
  type: 'sandbox-needs-init'
}

interface SandboxReadyMessage {
  type: 'sandbox-ready'
}

interface SandboxErrorMessage {
  type: 'sandbox-error'
  message: string
}

interface SandboxNavigateMessage {
  type: 'sandbox-navigate'
  href: string
  newTab: boolean
}

type InboundMessage =
  | SandboxNeedsInitMessage
  | SandboxReadyMessage
  | SandboxErrorMessage
  | SandboxNavigateMessage

function readDocumentTheme(): SandboxTheme {
  if (typeof document === 'undefined') return 'light'
  const attr = document.documentElement.getAttribute('data-theme')
  if (attr === 'dark') return 'dark'
  if (attr === 'light') return 'light'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

function originOf(url: string): string | null {
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

function isInboundMessage(value: unknown): value is InboundMessage {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  if (obj.type === 'sandbox-needs-init') return true
  if (obj.type === 'sandbox-ready') return true
  if (obj.type === 'sandbox-error' && typeof obj.message === 'string') return true
  if (
    obj.type === 'sandbox-navigate' &&
    typeof obj.href === 'string' &&
    typeof obj.newTab === 'boolean'
  ) {
    return true
  }
  return false
}

export interface SandboxFrameProps {
  code: string
  /**
   * Optional props spread into the rendered component. Used by the
   * Templates editor to preview a template with its stored
   * `default_values`; dashboards omit this entirely.
   */
  props?: Record<string, unknown>
  /**
   * Optional saved-templates registry forwarded into the sandbox so
   * user code's `<Template path="..." />` primitive can resolve them
   * at render time. Defaults to empty (composition disabled).
   */
  templates?: SandboxTemplateEntry[]
  /**
   * Saved dashboard id forwarded so operator controls can call useDbWrite.
   * Templates and artifacts omit it.
   */
  dashboardId?: string
}

const SandboxFrame = ({ code, props, templates, dashboardId }: SandboxFrameProps) => {
  const sandboxUrl = getDashboardSandboxUrl()
  const sandboxOrigin = sandboxUrl === '' ? null : originOf(sandboxUrl)

  // API-key deployments read from localStorage. Identity deployments use
  // the Redux token so refreshes immediately re-send the new JWT to an open
  // iframe instead of leaving it with an expired credential.
  const apiKey = getApiKey()
  const accessToken = useAppSelector((state) => state.authentication.token?.accessToken ?? '')
  const organizationId = getAuthenticationOrganizationId() ?? ''
  const demoMode = isDemoMode()
  const effectiveApiKey = demoMode && apiKey === '' ? 'demo' : apiKey
  // Dashboard code runs in the same realm as the sandbox data runtime. Do not
  // expose the broader Identity credential when the scoped API-key path wins.
  const sandboxAccessToken = effectiveApiKey === '' && accessToken !== '' ? accessToken : undefined
  const hasCredentials = effectiveApiKey !== '' || accessToken !== ''
  const identityEnabled = isIdentityEnabled()
  // useActiveBranch drives re-render when the user switches branch in
  // the global branch picker, so the sandbox stays branch-aware.
  const activeBranch = useActiveBranch()
  const branch = activeBranch ?? getOntologyBranchHeaderValue() ?? ''
  const activeSite = useActiveSite()
  const site = activeSite || getOntologySiteHeaderValue() || ''
  const proxyBaseUrl = getProxyBaseUrl()

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const [theme, setTheme] = useState<SandboxTheme>(() => readDocumentTheme())
  const [renderError, setRenderError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  // Stable identity for the props blob: the parent frequently passes a
  // fresh object literal (e.g. defaultValues parsed at every render),
  // and we only want to re-post when its contents actually changed.
  const propsKey = useMemo(() => JSON.stringify(props ?? null), [props])
  // Same idea for the templates registry — don't re-ship the registry
  // on every parent render.
  const templatesKey = useMemo(() => JSON.stringify(templates ?? []), [templates])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    const sync = () => setTheme(readDocumentTheme())
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme', 'class'] })
    return () => observer.disconnect()
  }, [])

  // Push the auth payload into the iframe. We send it eagerly on
  // every change (apiKey rotation, branch switch, proxy reconfig) so
  // the sandbox's hooks see the latest credentials on the next poll
  // tick. The sandbox also explicitly asks for init via
  // `sandbox-needs-init`; the listener below handles that case.
  useEffect(() => {
    if (!ready) return
    if (sandboxOrigin === null) return
    const win = iframeRef.current?.contentWindow
    if (!win) return
    const message: SandboxInitMessage = {
      type: 'sandbox-init',
      apiKey: effectiveApiKey,
      ...(sandboxAccessToken === undefined ? {} : { accessToken: sandboxAccessToken }),
      organizationId,
      proxyBaseUrl,
      branch,
      site,
      ...(dashboardId ? { dashboardId } : {})
    }
    win.postMessage(message, sandboxOrigin)
  }, [
    dashboardId,
    effectiveApiKey,
    branch,
    organizationId,
    proxyBaseUrl,
    ready,
    sandboxOrigin,
    sandboxAccessToken,
    site
  ])

  // Clone into structured-clone-safe plain data. RTK Query cache entries
  // can be Immer-frozen proxies; postMessage throws DataCloneError on
  // those, which silently leaves the sandbox registry empty and surfaces
  // as "No template found at …" for every <Template />.
  // templatesKey (not `templates`) is the dep — structural equality so we
  // don't re-clone on every parent render's new array identity.
  const plainTemplates = useMemo((): SandboxTemplateEntry[] => {
    const entries = templates ?? []
    try {
      return JSON.parse(JSON.stringify(entries)) as SandboxTemplateEntry[]
    } catch {
      return entries.map((t) => ({
        path: t.path,
        code: t.code,
        defaultValues:
          t.defaultValues !== null &&
          typeof t.defaultValues === 'object' &&
          !Array.isArray(t.defaultValues)
            ? { ...t.defaultValues }
            : {}
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- templatesKey tracks `templates`
  }, [templatesKey])

  // Push the templates registry whenever its contents change. The
  // sandbox replaces its in-process Map and re-renders against the
  // new registry, so a freshly-edited template propagates into any
  // open dashboard without reloading the page.
  useEffect(() => {
    if (!ready) return
    if (sandboxOrigin === null) return
    const win = iframeRef.current?.contentWindow
    if (!win) return
    const message: SandboxTemplatesMessage = {
      type: 'sandbox-templates',
      templates: plainTemplates
    }
    win.postMessage(message, sandboxOrigin)
  }, [plainTemplates, ready, sandboxOrigin])

  // Push every code/props/theme change into the iframe.
  useEffect(() => {
    if (!ready) return
    if (sandboxOrigin === null) return
    const win = iframeRef.current?.contentWindow
    if (!win) return
    const message: SandboxRenderMessage = {
      type: 'sandbox-render',
      code,
      theme,
      ...(props !== undefined ? { props } : {})
    }
    win.postMessage(message, sandboxOrigin)
    // propsKey is the structural-equality marker for `props`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, propsKey, theme, ready, sandboxOrigin])

  useEffect(() => {
    if (sandboxOrigin === null) return
    const postBootstrap = (win: Window): void => {
      const init: SandboxInitMessage = {
        type: 'sandbox-init',
        apiKey: effectiveApiKey,
        ...(sandboxAccessToken === undefined ? {} : { accessToken: sandboxAccessToken }),
        organizationId,
        proxyBaseUrl,
        branch,
        site,
        ...(dashboardId ? { dashboardId } : {})
      }
      win.postMessage(init, sandboxOrigin)
      // Sandbox reloads (HMR / iframe remount) clear the in-iframe
      // registry. ready may already be true, so the templates/render
      // effects would no-op — re-push both on every handshake.
      win.postMessage(
        { type: 'sandbox-templates', templates: plainTemplates } satisfies SandboxTemplatesMessage,
        sandboxOrigin
      )
      win.postMessage(
        {
          type: 'sandbox-render',
          code,
          theme,
          ...(props !== undefined ? { props } : {})
        } satisfies SandboxRenderMessage,
        sandboxOrigin
      )
    }
    const handle = (event: MessageEvent): void => {
      if (event.origin !== sandboxOrigin) return
      if (event.source !== iframeRef.current?.contentWindow) return
      if (!isInboundMessage(event.data)) return
      const data = event.data
      if (data.type === 'sandbox-ready') {
        setReady(true)
        setRenderError(null)
        const win = iframeRef.current?.contentWindow
        if (win) postBootstrap(win)
        return
      }
      if (data.type === 'sandbox-error') {
        setRenderError(data.message)
        return
      }
      if (data.type === 'sandbox-navigate') {
        const resolution = resolveDashboardLink(data.href, {
          origin: window.location.origin,
          href: window.location.href
        })
        if (resolution === null) return
        followDashboardLink(
          resolution,
          data.newTab,
          {
            assign: (url) => window.location.assign(url),
            open: (url) => {
              window.open(url, '_blank', 'noopener,noreferrer')
            }
          },
          window.location.origin
        )
        return
      }
      // sandbox-needs-init: post the current auth payload back. This
      // covers the case where the sandbox mounts after we'd already
      // set `ready` (no re-effect would fire) — the explicit ask
      // means the handshake works regardless of mount order.
      const win = iframeRef.current?.contentWindow
      if (!win) return
      postBootstrap(win)
    }
    window.addEventListener('message', handle)
    return () => {
      window.removeEventListener('message', handle)
    }
    // apiKey/branch/site/proxyBaseUrl deliberately included — the handler
    // closes over them, so a stale closure would post an out-of-date
    // init when the sandbox asks. React's effect cleanup tears down
    // the listener cleanly.
  }, [
    dashboardId,
    effectiveApiKey,
    branch,
    organizationId,
    proxyBaseUrl,
    sandboxAccessToken,
    sandboxOrigin,
    site,
    plainTemplates,
    code,
    theme,
    props
  ])

  useEffect(() => {
    setRenderError(null)
  }, [code])

  if (sandboxUrl === '' || sandboxOrigin === null) {
    return (
      <div className="self-center p-6 text-center text-sm text-ink-3">
        Dashboard sandbox URL is not configured. Set <code>DASHBOARD_SANDBOX_URL</code> in{' '}
        <code>config.js</code>.
      </div>
    )
  }

  // Hard gate: the cross-origin sandbox must receive either the identity
  // Bearer token or a tenant API key over postMessage before it can fetch.
  if (!hasCredentials) {
    return (
      <div className="flex h-full w-full items-center justify-center p-6">
        <div className="max-w-md rounded-md border border-line bg-bg-2 p-4 text-sm text-ink-2">
          <div className="mb-1 font-medium text-ink-1">Authentication required</div>
          <p>
            {identityEnabled
              ? 'Sign in with Identity to enable live dashboard rendering.'
              : 'Open Settings → General and enter your HMAC API key under Proxy server connection to enable live dashboard rendering.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative h-full w-full" data-tour-id="dashboard-render-surface">
      <iframe
        ref={iframeRef}
        src={sandboxUrl}
        title="Dashboard render sandbox"
        sandbox="allow-scripts allow-same-origin"
        onLoad={() => {
          setReady(true)
          setRenderError(null)
        }}
        className="block h-full w-full border-0 bg-bg"
      />
      {renderError !== null && (
        <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center p-3">
          <div className="pointer-events-auto rounded-md border border-st-down/40 bg-st-down-bg px-3 py-1.5 text-xs text-st-down shadow-sm">
            Couldn&apos;t render: {renderError}
          </div>
        </div>
      )}
    </div>
  )
}

export default SandboxFrame
